import React, { useCallback, useEffect, useState } from 'react'
import {
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CFormText,
  CLoadingButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSmartTable,
} from '@coreui/react-pro'

import ToastComponent from 'src/components/common/TaostComponent.js'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import RowActionsMenu from 'src/components/common/RowActionsMenu'
import EmailQuotaService from 'src/service/EmailQuotaService'
import '../User/User.css'

// getAdminEmailQuotaUsers only filters on these.
const SEARCH_FIELDS = [
  { key: 'game_name', label: 'Game Name' },
  { key: 'full_name', label: 'Hub Admin' },
  { key: 'email', label: 'Email' },
]

const GAME_TYPES = {
  1: 'Season Game',
  2: 'Tournament Game (For Leagues)',
  3: 'Tournament Game (For Indoor Leagues)',
  4: 'Match Contest',
}

const columns = [
  { label: 'Hub Name', key: 'club_name', filter: false },
  { label: 'Game Name', key: 'game_name', filter: false },
  { label: 'Game Type', key: 'game_type', filter: false },
  { label: 'Email', key: 'email', filter: false },
  {
    label: 'Email Quota',
    key: 'email_quota',
    filter: false,
    _style: { textAlign: 'right' },
  },
  { label: 'Email Alerts', key: 'membership_is_paid', filter: false },
  { label: 'Actions', key: 'action', filter: false, sorter: false, _style: { width: '1%' } },
]

const QuotaModal = ({ game, onClose, onSaved }) => {
  const [quota, setQuota] = useState('')
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!game) return
    setQuota(game.email_quota ?? '')
    setError(null)
  }, [game])

  const handleSubmit = (e) => {
    e.preventDefault()
    const value = Number(quota)
    if (quota === '' || !Number.isInteger(value) || value < 1) {
      setError('Enter a whole number of emails, 1 or more.')
      return
    }
    setSaving(true)
    EmailQuotaService.adminEmailQuotaActivation({ gameId: game.game_id, email_quota: value })
      .then((res) => {
        if (res.status === 200) {
          ToastComponent(res.message, 'success')
          onSaved()
          onClose()
        } else {
          ToastComponent(res.message || 'Could not update the email quota', 'error')
        }
      })
      .catch((err) => {
        ToastComponent(err?.response?.data?.message || 'Could not update the email quota', 'error')
      })
      .finally(() => setSaving(false))
  }

  const isActive = !!game?.membership_is_paid

  return (
    <CModal visible={!!game} onClose={onClose} alignment="center" backdrop="static">
      <CForm onSubmit={handleSubmit} noValidate>
        <CModalHeader>
          <CModalTitle>{isActive ? 'Update email quota' : 'Activate email alerts'}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p className="um-muted mb-3">
            <strong className="text-body">{game?.game_name}</strong>
            {game?.club_name ? ` · ${game.club_name}` : ''}
          </p>
          <CFormLabel htmlFor="emailQuota">Email quota</CFormLabel>
          <CFormInput
            type="number"
            id="emailQuota"
            min={1}
            step={1}
            placeholder="e.g. 5000"
            value={quota}
            invalid={!!error}
            autoFocus
            onChange={(e) => {
              setQuota(e.target.value)
              setError(null)
            }}
          />
          {error ? (
            <CFormFeedback invalid>{error}</CFormFeedback>
          ) : (
            <CFormText>
              How many alert emails this game can send. Saving marks email alerts as active, free of
              charge.
            </CFormText>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </CButton>
          <CLoadingButton color="primary" type="submit" loading={saving}>
            {isActive ? 'Save Quota' : 'Activate'}
          </CLoadingButton>
        </CModalFooter>
      </CForm>
    </CModal>
  )
}

const AdminEmailUsers = () => {
  const [loading, setLoading] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState({})
  const [searchField, setSearchField] = useState('game_name')
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [users, setUsers] = useState()
  const [editingGame, setEditingGame] = useState(null)

  const clearSearchKeys = (filter) => {
    const next = { ...filter }
    SEARCH_FIELDS.forEach((f) => delete next[f.key])
    return next
  }

  const applySearch = (value) => {
    setActivePage(1)
    const next = clearSearchKeys(columnFilter)
    if (value) next[searchField] = value
    setColumnFilter(next)
  }

  const handleFieldChange = (key) => {
    setSearchField(key)
    setColumnFilter((prev) => clearSearchKeys(prev))
  }

  const fetchData = useCallback(() => {
    const offset = itemsPerPage * activePage - itemsPerPage
    const params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      if (columnFilter[key]) params.append(key, columnFilter[key])
    })
    if (columnSorter && columnSorter.column !== undefined) {
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
    }

    setLoading(true)
    EmailQuotaService.adminEmailUsers(offset, itemsPerPage, activePage, params)
      .then((result) => setUsers(result.data))
      .catch((e) => {
        ToastComponent(e?.response?.data?.message || 'Could not load games', 'error')
      })
      .finally(() => setLoading(false))
  }, [activePage, columnFilter, columnSorter, itemsPerPage])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const activeSearchLabel = SEARCH_FIELDS.find((f) => f.key === searchField)?.label

  return (
    <>
      <div className="ge-page-header">
        <h1 className="ge-page-title">Activate Email Alerts</h1>
        <p className="ge-page-subtitle">
          Switch on email alerts for a hub&apos;s game and set how many emails it can send.
        </p>
      </div>

      <CCard className="um-panel">
        <CCardBody>
          <div className="um-panel-heading">
            <div>
              <h2 className="um-panel-title">
                Hub Games
                {users?.total !== undefined && <span className="ge-tab-count">{users.total}</span>}
              </h2>
              <p className="um-panel-note">One row per game of every active hub.</p>
            </div>
          </div>

          <TableSearchToolbar
            fields={SEARCH_FIELDS}
            field={searchField}
            onFieldChange={handleFieldChange}
            value={columnFilter?.[searchField] || ''}
            onSearch={applySearch}
            placeholder={`Search by ${activeSearchLabel}...`}
          />

          <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
            <CSmartTable
              columns={columns}
              columnSorter={{ external: true }}
              noItemsLabel="No hub games found"
              scopedColumns={{
                club_name: (item) => <td>{item.club_name || '-'}</td>,
                game_name: (item) =>
                  item.game_id ? (
                    <td className="fw-semibold">{item.game_name}</td>
                  ) : (
                    <td className="um-muted fst-italic">No game yet</td>
                  ),
                game_type: (item) => <td>{GAME_TYPES[item.game_type] || '-'}</td>,
                email: (item) => <td>{item.email || '-'}</td>,
                email_quota: (item) => (
                  <td className="text-end pe-4">
                    {item.email_quota != null ? Number(item.email_quota).toLocaleString() : '-'}
                  </td>
                ),
                membership_is_paid: (item) => (
                  <td>
                    <CBadge
                      color={item.membership_is_paid ? 'success' : 'secondary'}
                      shape="rounded-pill"
                      className="px-2 py-1"
                    >
                      {item.membership_is_paid ? 'Active' : 'Not active'}
                    </CBadge>
                  </td>
                ),
                action: (item) => (
                  <td className="text-nowrap">
                    <RowActionsMenu
                      actions={[
                        {
                          label: item.membership_is_paid ? 'Edit Quota' : 'Activate',
                          disabled: !item.game_id,
                          onClick: () => setEditingGame(item),
                        },
                      ]}
                    />
                  </td>
                ),
              }}
              items={users?.data}
              itemsPerPage={itemsPerPage}
              itemsPerPageSelect
              loading={loading}
              pagination={{ external: true }}
              paginationProps={{
                activePage,
                pages: Math.ceil(users?.total / itemsPerPage) || 1,
              }}
              tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
              onActivePageChange={(page) => setActivePage(page)}
              onItemsPerPageChange={(perPage) => {
                setActivePage(1)
                setItemsPerPage(perPage)
              }}
              onSorterChange={(sorter) => setColumnSorter(sorter)}
            />
          </div>
        </CCardBody>
      </CCard>

      <QuotaModal game={editingGame} onClose={() => setEditingGame(null)} onSaved={fetchData} />
    </>
  )
}

export default AdminEmailUsers
